import React, { useState, useMemo } from 'react';
import { X, Eye, EyeOff, Plus, Settings, Trash2, ChevronUp, ChevronDown, FolderOpen, FolderClosed, LayoutGrid } from 'lucide-react';
import { FormTemplate, FieldSchema } from '../../types/formSchema';

interface FormEditorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sectionId: string;
  sectionTitle: string;
  builtInFields?: Array<{ id: string; label: string; type: string; required: boolean }>;
  builtInFieldSections?: Array<{ id: string; title: string; isBuiltIn: true; fields: string[] }>; // Semantic sections for built-in fields
  customFields: FieldSchema[];
  hiddenFields: string[];
  hiddenSections?: string[]; // New: track hidden sections
  sectionOrder?: string[]; // Order of section IDs (built-in + custom)
  // New: unified fields list (built-in + custom in order)
  allFields?: Array<(FieldSchema | { id: string; label: string; type: string; required: boolean }) & { isCustom: boolean }>;
  onToggleFieldVisibility: (fieldId: string) => void;
  onToggleSectionVisibility?: (sectionName: string) => void; // New: toggle section visibility
  onAddCustomField: (position?: number) => void;
  onEditCustomField: (field: FieldSchema) => void;
  onDeleteCustomField: (fieldId: string) => void;
  onReorderCustomFields: (fromIndex: number, toIndex: number) => void;
  onReorderBuiltInFields: (fromIndex: number, toIndex: number) => void;
  onReorderSections?: (fromIndex: number, toIndex: number) => void; // Reorder sections
  onReorderFieldsInSection?: (sectionId: string, fromIndex: number, toIndex: number) => void; // Reorder fields within a section
  // Page reordering
  onEnterReorderMode?: () => void;
  isReorderMode?: boolean;
}

export const FormEditorPanel: React.FC<FormEditorPanelProps> = ({
  isOpen,
  onClose,
  sectionId,
  sectionTitle,
  builtInFields = [],
  builtInFieldSections = [],
  customFields,
  hiddenFields,
  hiddenSections = [],
  sectionOrder,
  allFields, // New unified approach
  onToggleFieldVisibility,
  onToggleSectionVisibility,
  onAddCustomField,
  onEditCustomField,
  onDeleteCustomField,
  onReorderCustomFields,
  onReorderBuiltInFields,
  onReorderSections,
  onReorderFieldsInSection,
  onEnterReorderMode,
  isReorderMode = false,
}) => {
  // Track collapsed sections locally
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  
  // Group custom fields by section
  const fieldsBySection = useMemo(() => {
    const grouped: Record<string, FieldSchema[]> = {};
    const unsectioned: FieldSchema[] = [];
    
    customFields.forEach(field => {
      if (field.section) {
        if (!grouped[field.section]) {
          grouped[field.section] = [];
        }
        grouped[field.section].push(field);
      } else {
        unsectioned.push(field);
      }
    });
    
    // Sort fields within each section by sectionOrder
    Object.keys(grouped).forEach(sectionName => {
      grouped[sectionName].sort((a, b) => (a.sectionOrder || 0) - (b.sectionOrder || 0));
    });
    
    return { grouped, unsectioned };
  }, [customFields]);
  
  // Get all section names
  const allSectionNames = Object.keys(fieldsBySection.grouped).sort();
  
  // Create unified sections list (built-in + custom sections)
  const unifiedSections = useMemo(() => {
    console.log('[FormEditorPanel] ========== Building unifiedSections ==========');
    console.log('[FormEditorPanel] 📂 Built-in sections count:', builtInFieldSections.length);
    console.log('[FormEditorPanel] 📂 Built-in sections:', builtInFieldSections);
    console.log('[FormEditorPanel] 📂 Custom sections grouped:', fieldsBySection.grouped);
    console.log('[FormEditorPanel] 📂 Unsectioned custom fields:', fieldsBySection.unsectioned);
    
    const sections: Array<{
      id: string;
      title: string;
      isBuiltIn: boolean;
      fields?: string[]; // For built-in sections
      customFields?: FieldSchema[]; // For custom sections
    }> = [];
    
    // Add built-in sections
    builtInFieldSections.forEach(section => {
      console.log('[FormEditorPanel] 📂 Adding built-in section:', section.id, section.title);
      sections.push({
        id: section.id,
        title: section.title,
        isBuiltIn: true,
        fields: section.fields,
      });
    });
    
    // Add custom sections
    Object.keys(fieldsBySection.grouped).forEach(sectionName => {
      console.log('[FormEditorPanel] 📂 Adding custom section:', sectionName, 'with', fieldsBySection.grouped[sectionName].length, 'fields');
      sections.push({
        id: sectionName, // Use section name as ID for custom sections
        title: sectionName,
        isBuiltIn: false,
        customFields: fieldsBySection.grouped[sectionName],
      });
    });
    
    // Add unsectioned custom fields as "Additional Fields" section
    if (fieldsBySection.unsectioned.length > 0) {
      console.log('[FormEditorPanel] 📂 Adding unsectioned section with', fieldsBySection.unsectioned.length, 'fields');
      sections.push({
        id: 'custom_fields_unsectioned',
        title: 'Additional Fields',
        isBuiltIn: false,
        customFields: fieldsBySection.unsectioned,
      });
    }
    
    console.log('[FormEditorPanel] 📂 Total sections before sorting:', sections.length);
    console.log('[FormEditorPanel] 📂 Sections:', sections.map(s => `${s.id} (${s.isBuiltIn ? 'built-in' : 'custom'})`));
    console.log('[FormEditorPanel] 📂 Section order:', sectionOrder);
    
    // If we have a sectionOrder, use it to sort
    if (sectionOrder && sectionOrder.length > 0) {
      sections.sort((a, b) => {
        const aIndex = sectionOrder.indexOf(a.id);
        const bIndex = sectionOrder.indexOf(b.id);
        
        // If both are in the order, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        // If only a is in the order, it comes first
        if (aIndex !== -1) return -1;
        // If only b is in the order, it comes first
        if (bIndex !== -1) return 1;
        // If neither is in the order, maintain current order
        return 0;
      });
      console.log('[FormEditorPanel] 📂 Sections after sorting:', sections.map(s => s.id));
    }
    
    console.log('[FormEditorPanel] ========== unifiedSections built ==========');
    return sections;
  }, [builtInFieldSections, fieldsBySection.grouped, fieldsBySection.unsectioned, sectionOrder]);
  
  // Use unified fields if provided, otherwise fall back to separate lists
  const useUnifiedApproach = allFields && allFields.length > 0;
  const handleMoveBuiltInUp = (index: number) => {
    if (index > 0) {
      onReorderBuiltInFields(index, index - 1);
    }
  };

  const handleMoveBuiltInDown = (index: number, totalFields: number) => {
    if (index < totalFields - 1) {
      onReorderBuiltInFields(index, index + 1);
    }
  };

  const handleMoveCustomUp = (index: number) => {
    if (index > 0) {
      onReorderCustomFields(index, index - 1);
    }
  };

  const handleMoveCustomDown = (index: number, totalFields: number) => {
    if (index < totalFields - 1) {
      onReorderCustomFields(index, index + 1);
    }
  };

  // Unified approach: move any field up/down
  const handleMoveUp = (index: number) => {
    if (index > 0) {
      onReorderBuiltInFields(index, index - 1);
    }
  };

  const handleMoveDown = (index: number, totalFields: number) => {
    if (index < totalFields - 1) {
      onReorderBuiltInFields(index, index + 1);
    }
  };


  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-[9998] transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Side Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-[9999] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        <div className="flex flex-col h-full max-h-screen">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-500 to-blue-600">
            <div>
              <h2 className="text-lg font-bold text-white">Edit Form Fields</h2>
              <p className="text-sm text-blue-100">{sectionTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-blue-400 rounded-lg transition-colors text-white"
              title="Close editor"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-3">
            {/* Reorder Pages Button */}
            {onEnterReorderMode && !isReorderMode && (
              <button
                onClick={onEnterReorderMode}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 transition-all"
              >
                <LayoutGrid size={18} />
                <span>Reorder Pages</span>
              </button>
            )}
            
            {/* Reorder Mode Active Message */}
            {isReorderMode && (
              <div className="p-4 bg-purple-100 border-2 border-purple-300 rounded-lg">
                <div className="flex items-center gap-2 text-purple-800 font-medium mb-1">
                  <LayoutGrid size={16} />
                  <span>Reorder Mode Active</span>
                </div>
                <p className="text-sm text-purple-700">Use the left/right arrow buttons on each page in the navigation menu to reorder them.</p>
              </div>
            )}
            
            {/* Unified Sections - All sections (built-in + custom) with reordering */}
            {useUnifiedApproach && unifiedSections.map((section, sectionIndex) => {
              const isCollapsed = collapsedSections.has(section.id);
              const isSectionHidden = hiddenSections.includes(section.id);
              
              // Get fields for this section
              let sectionFields: any[] = [];
              if (section.isBuiltIn && section.fields) {
                // Built-in section: filter allFields by field IDs
                sectionFields = allFields.filter(f => section.fields!.includes(f.id));
              } else if (!section.isBuiltIn && section.customFields) {
                // Custom section: use customFields directly
                sectionFields = section.customFields;
              }
              
              if (sectionFields.length === 0) return null;
              
              const isBuiltInSection = section.isBuiltIn;
              const borderColor = isBuiltInSection ? 'border-gray-300' : 'border-indigo-200';
              const bgColor = isBuiltInSection ? 'bg-gray-50' : 'bg-indigo-50/50';
              const headerBgColor = isBuiltInSection ? 'bg-gray-100' : 'bg-indigo-100/50';
              const headerBorderColor = isBuiltInSection ? 'border-gray-300' : 'border-indigo-200';
              const sectionIcon = isBuiltInSection ? '📋' : '📂';
              
              return (
                <div key={section.id} className={`border ${borderColor} rounded-lg ${bgColor}`}>
                  {/* Section Header */}
                  <div className={`flex items-center justify-between p-3 ${headerBgColor} rounded-t-lg border-b ${headerBorderColor}`}>
                    {/* Section Reordering Arrows - Inline */}
                    {onReorderSections && (
                      <div className="flex flex-col gap-0.5 mr-2">
                        <button
                          onClick={() => {
                            console.log('[FormEditorPanel] 🔼 UP button clicked for section:', section.id, section.title);
                            console.log('[FormEditorPanel] Section index:', sectionIndex, 'Total sections:', unifiedSections.length);
                            console.log('[FormEditorPanel] Will call: onReorderSections(', sectionIndex, ',', sectionIndex - 1, ')');
                            if (sectionIndex > 0) {
                              onReorderSections(sectionIndex, sectionIndex - 1);
                            } else {
                              console.log('[FormEditorPanel] ❌ Already at top, cannot move up');
                            }
                          }}
                          disabled={sectionIndex === 0}
                          className={`p-1 rounded border transition-all ${
                            sectionIndex === 0
                              ? 'text-gray-300 border-gray-200 cursor-not-allowed bg-gray-50'
                              : 'text-blue-600 border-blue-300 hover:bg-blue-100 hover:border-blue-500 bg-white shadow-sm'
                          }`}
                          title="Move section up"
                        >
                          <ChevronUp size={18} strokeWidth={2.5} />
                        </button>
                        <button
                          onClick={() => {
                            console.log('[FormEditorPanel] 🔽 DOWN button clicked for section:', section.id, section.title);
                            console.log('[FormEditorPanel] Section index:', sectionIndex, 'Total sections:', unifiedSections.length);
                            console.log('[FormEditorPanel] Will call: onReorderSections(', sectionIndex, ',', sectionIndex + 1, ')');
                            if (sectionIndex < unifiedSections.length - 1) {
                              onReorderSections(sectionIndex, sectionIndex + 1);
                            } else {
                              console.log('[FormEditorPanel] ❌ Already at bottom, cannot move down');
                            }
                          }}
                          disabled={sectionIndex === unifiedSections.length - 1}
                          className={`p-1 rounded border transition-all ${
                            sectionIndex === unifiedSections.length - 1
                              ? 'text-gray-300 border-gray-200 cursor-not-allowed bg-gray-50'
                              : 'text-blue-600 border-blue-300 hover:bg-blue-100 hover:border-blue-500 bg-white shadow-sm'
                          }`}
                          title="Move section down"
                        >
                          <ChevronDown size={18} strokeWidth={2.5} />
                        </button>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 flex-1">
                      <button
                        onClick={() => {
                          const newCollapsed = new Set(collapsedSections);
                          if (isCollapsed) {
                            newCollapsed.delete(section.id);
                          } else {
                            newCollapsed.add(section.id);
                          }
                          setCollapsedSections(newCollapsed);
                        }}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title={isCollapsed ? 'Expand section' : 'Collapse section'}
                      >
                        {isCollapsed ? <FolderClosed size={14} /> : <FolderOpen size={14} />}
                      </button>
                      <span className={`text-sm font-semibold ${isSectionHidden ? 'line-through text-gray-500' : isBuiltInSection ? 'text-gray-700' : 'text-indigo-900'}`}>
                        {sectionIcon} {section.title}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${isBuiltInSection ? 'text-gray-600 bg-gray-200' : 'text-indigo-600 bg-indigo-100'}`}>
                        {sectionFields.length} field{sectionFields.length !== 1 ? 's' : ''}
                      </span>
                      {isSectionHidden && (
                        <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
                          Hidden
                        </span>
                      )}
                    </div>
                    {!isBuiltInSection && onToggleSectionVisibility && (
                      <button
                        onClick={() => onToggleSectionVisibility(section.id)}
                        className={`p-1.5 rounded transition-colors ${
                          isSectionHidden
                            ? 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                            : 'text-indigo-600 hover:text-orange-600 hover:bg-orange-50'
                        }`}
                        title={isSectionHidden ? 'Show section' : 'Hide section'}
                      >
                        {isSectionHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    )}
                  </div>
                  
                  {/* Section Fields */}
                  {!isCollapsed && (
                    <div className="p-2 space-y-2">
                      {sectionFields.map((field, fieldIndex) => {
                        const isHidden = hiddenFields.includes(field.id);
                        const isCustomField = !isBuiltInSection || field.isCustom;
                        
                        return (
                          <div key={field.id} className="relative pl-8">
                            {/* Field Reordering Arrows - Only for custom fields or if handler provided */}
                            {(isCustomField && onReorderFieldsInSection) && (
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col gap-0.5">
                                <button
                                  onClick={() => fieldIndex > 0 && onReorderFieldsInSection(section.id, fieldIndex, fieldIndex - 1)}
                                  disabled={fieldIndex === 0}
                                  className={`p-0.5 rounded transition-colors ${
                                    fieldIndex === 0
                                      ? 'text-gray-300 cursor-not-allowed'
                                      : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-100'
                                  }`}
                                  title="Move field up"
                                >
                                  <ChevronUp size={14} />
                                </button>
                                <button
                                  onClick={() => fieldIndex < sectionFields.length - 1 && onReorderFieldsInSection(section.id, fieldIndex, fieldIndex + 1)}
                                  disabled={fieldIndex === sectionFields.length - 1}
                                  className={`p-0.5 rounded transition-colors ${
                                    fieldIndex === sectionFields.length - 1
                                      ? 'text-gray-300 cursor-not-allowed'
                                      : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-100'
                                  }`}
                                  title="Move field down"
                                >
                                  <ChevronDown size={14} />
                                </button>
                              </div>
                            )}
                            
                            <div className={`group p-2.5 rounded-lg border ${isCustomField ? 'border-indigo-200 bg-white hover:bg-indigo-50' : 'border-gray-200 bg-white hover:bg-gray-50'} hover:border-${isCustomField ? 'indigo' : 'gray'}-400 hover:shadow-sm transition-all`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`font-medium text-sm ${isHidden ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                                      {field.label}
                                    </span>
                                    {field.required && !isHidden && (
                                      <span className="text-red-500 text-xs">*</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                                      {field.type}
                                    </span>
                                    {isHidden && (
                                      <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
                                        Hidden
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  {isCustomField && (
                                    <>
                                      <button
                                        onClick={() => onEditCustomField(field)}
                                        className={`p-1.5 text-gray-400 hover:text-${isBuiltInSection ? 'indigo' : 'blue'}-600 hover:bg-${isBuiltInSection ? 'indigo' : 'blue'}-100 rounded transition-colors`}
                                        title="Edit field"
                                      >
                                        <Settings size={14} />
                                      </button>
                                      <button
                                        onClick={() => {
                                          if (window.confirm(`Delete "${field.label}"?`)) {
                                            onDeleteCustomField(field.id);
                                          }
                                        }}
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                        title="Delete field"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </>
                                  )}
                                  <button
                                    onClick={() => onToggleFieldVisibility(field.id)}
                                    className={`p-1.5 rounded transition-colors ${
                                      isHidden
                                        ? 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                                        : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                                    }`}
                                    title={isHidden ? 'Show field' : 'Hide field'}
                                  >
                                    {isHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Individual Custom Fields (no section) */}
            {useUnifiedApproach && fieldsBySection.unsectioned.map((field, index) => (
                      <div key={field.id} className="relative pl-8">
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col gap-0.5">
                          <button
                            onClick={() => handleMoveCustomUp(customFields.findIndex(f => f.id === field.id))}
                            disabled={index === 0 && allSectionNames.length === 0}
                            className={`p-0.5 rounded transition-colors ${
                              index === 0 && allSectionNames.length === 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title="Move up"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            onClick={() => handleMoveCustomDown(customFields.findIndex(f => f.id === field.id), customFields.length)}
                            disabled={index === fieldsBySection.unsectioned.length - 1}
                            className={`p-0.5 rounded transition-colors ${
                              index === fieldsBySection.unsectioned.length - 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title="Move down"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                        <div className="group p-3 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 hover:shadow-sm transition-all">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-gray-800">
                                  {field.label}
                                </span>
                                {field.required && (
                                  <span className="text-red-500 text-xs">*</span>
                                )}
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded font-medium">
                                  Custom
                                </span>
                              </div>
                              <span className="text-xs text-gray-600 bg-blue-100 px-2 py-0.5 rounded inline-block mt-1">
                                {field.type}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => onEditCustomField(field)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-200 rounded transition-colors"
                                          title="Edit field"
                                        >
                                          <Settings size={14} />
                                        </button>
                                        <button
                                          onClick={() => {
                                            if (window.confirm(`Delete "${field.label}"?`)) {
                                              onDeleteCustomField(field.id);
                                            }
                                          }}
                                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                          title="Delete field"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))} 

            {/* Individual Custom Fields (no section) */}
            {useUnifiedApproach && fieldsBySection.unsectioned.map((field, index) => (
                      <div key={field.id} className="relative pl-8">
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col gap-0.5">
                          <button
                            onClick={() => handleMoveCustomUp(customFields.findIndex(f => f.id === field.id))}
                            disabled={index === 0 && allSectionNames.length === 0}
                            className={`p-0.5 rounded transition-colors ${
                              index === 0 && allSectionNames.length === 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title="Move up"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            onClick={() => handleMoveCustomDown(customFields.findIndex(f => f.id === field.id), customFields.length)}
                            disabled={index === fieldsBySection.unsectioned.length - 1}
                            className={`p-0.5 rounded transition-colors ${
                              index === fieldsBySection.unsectioned.length - 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title="Move down"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                        <div className="group p-3 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 hover:shadow-sm transition-all">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-gray-800">
                                  {field.label}
                                </span>
                                {field.required && (
                                  <span className="text-red-500 text-xs">*</span>
                                )}
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded font-medium">
                                  Custom
                                </span>
                              </div>
                              <span className="text-xs text-gray-600 bg-blue-100 px-2 py-0.5 rounded inline-block mt-1">
                                {field.type}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => onEditCustomField(field)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-200 rounded transition-colors"
                                title="Edit field"
                              >
                                <Settings size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Delete "${field.label}"?`)) {
                                    onDeleteCustomField(field.id);
                                  }
                                }}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete field"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

            {/* Add Field Button */}
            {useUnifiedApproach && (
              <div className="flex justify-center pt-2">
                <button
                  onClick={() => onAddCustomField()}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border-2 border-dashed border-blue-300 hover:border-blue-400 rounded-lg transition-all"
                >
                  <Plus size={16} />
                  Add Custom Field
                </button>
              </div>
            )}

            {!useUnifiedApproach && (
              /* Legacy: Separate built-in and custom fields */
              <div className="space-y-6">
            {/* Built-in Fields Section */}
            {builtInFields.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Built-in Fields ({builtInFields.length})
                </h3>
                <div className="space-y-2 pl-10">
                  {builtInFields.map((field, index) => {
                    const isHidden = hiddenFields.includes(field.id);
                    return (
                      <React.Fragment key={field.id}>
                      <div
                        className="relative"
                      >
                        {/* Arrow buttons on the left */}
                        <div className="absolute left-[-32px] top-1/2 -translate-y-1/2 flex flex-col gap-0.5">
                          <button
                            onClick={() => handleMoveBuiltInUp(index)}
                            disabled={index === 0}
                            className={`p-0.5 rounded transition-colors ${
                              index === 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title="Move up"
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            onClick={() => handleMoveBuiltInDown(index, builtInFields.length)}
                            disabled={index === builtInFields.length - 1}
                            className={`p-0.5 rounded transition-colors ${
                              index === builtInFields.length - 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title="Move down"
                          >
                            <ChevronDown size={16} />
                          </button>
                        </div>

                        <div
                          className={`group p-3 rounded-lg border transition-all ${
                            isHidden
                              ? 'bg-gray-50 border-gray-200 opacity-60'
                              : 'bg-white border-gray-300 hover:border-blue-400 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium text-sm ${isHidden ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                                  {field.label}
                                </span>
                                {field.required && !isHidden && (
                                  <span className="text-red-500 text-xs">*</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                  {field.type}
                                </span>
                                {isHidden && (
                                  <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                                    Hidden
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => onToggleFieldVisibility(field.id)}
                              className={`p-1.5 rounded transition-colors ${
                                isHidden
                                  ? 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                                  : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                              }`}
                              title={isHidden ? 'Show field' : 'Hide field'}
                            >
                              {isHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Insertion button after this field */}
                      <div className="flex items-center justify-center py-1">
                        <button
                          onClick={() => onAddCustomField(index + 1)}
                          className="group flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 hover:border-blue-400 rounded-full transition-all shadow-sm hover:shadow"
                          title="Add custom field here"
                        >
                          <Plus size={14} className="group-hover:scale-110 transition-transform" />
                          <span>Add Field</span>
                        </button>
                      </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
              
              {/* Empty State - Show when no fields exist */}
              {!useUnifiedApproach && builtInFields.length === 0 && customFields.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Settings size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Fields Yet</h3>
                  <p className="text-sm text-gray-500 mb-6 max-w-sm">
                    This page doesn't have any custom fields yet. Click the button below to add your first custom field.
                  </p>
                  <button
                    onClick={() => onAddCustomField()}
                    className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-md hover:shadow-lg"
                  >
                    <Plus size={18} />
                    Add Your First Custom Field
                  </button>
                </div>
              )}
              
              {/* Add Custom Field Button - Show when legacy mode has some content */}
              {!useUnifiedApproach && (builtInFields.length > 0 || customFields.length > 0) && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={() => onAddCustomField()}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border-2 border-dashed border-blue-300 hover:border-blue-400 rounded-lg transition-all"
                  >
                    <Plus size={16} />
                    Add Custom Field
                  </button>
                </div>
              )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>Changes save automatically</span>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
